import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, FileText, Pencil, Trash2 } from 'lucide-react';
import type {
  GroupContentSummary,
  HydratedGroupItem,
  SlotNode,
  SlotPreset,
} from '@news-cms/shared';
import { SLOT_PRESETS } from '@news-cms/shared';
import {
  groupContentPreviewQueryOptions,
  groupContentsListQueryOptions,
} from '@/lib/group-contents';
import { ItemSlot } from '@/components/homepage/layout-preview';
import { SlotGroupEditor } from '@/components/homepage/slot-group-editor';
import { useEditor } from './editor-context';
import { NodeToolbar } from './node-toolbar';

export function SlotNodeEditor({ node }: { node: SlotNode }) {
  const { selectedId, select, updateSlot, remove } = useEditor();
  const isSelected = selectedId === node.id;

  const groupsQuery = useQuery(groupContentsListQueryOptions({ page: 1, pageSize: 100 }));
  const groups: GroupContentSummary[] = groupsQuery.data?.items ?? [];
  const selectedGroup = groups.find((g) => g.slug === node.groupSlug) ?? null;

  // Position-based item picker — labels are generic ("ข่าวที่ 1, 2, 3...")
  const maxCount = selectedGroup?.maxCount ?? 1;
  const itemOptions = Array.from({ length: maxCount }, (_, i) => i);

  // Preview items needed for the inline single-item render (filtered by
  // slot.itemIndex) — fetched only when a group is selected.
  const previewQuery = useQuery({
    ...groupContentPreviewQueryOptions(selectedGroup?.id ?? ''),
    enabled: !!selectedGroup,
  });
  const previewItems: HydratedGroupItem[] = previewQuery.data ?? [];
  // Defensive fallbacks: legacy slots saved before this PR don't have
  // preset/itemIndex set in the layout JSON; the UI dropdowns default
  // to 'hero'/0 visually but the data stays undefined until admin
  // explicitly changes them. Default at render-time so the preview
  // still resolves to item 0 with 'card' style.
  const effectiveIndex = typeof node.itemIndex === 'number' ? node.itemIndex : 0;
  const effectivePreset = node.preset ?? 'card';
  const selectedItem = previewItems[effectiveIndex] ?? undefined;

  // Edit modal state — opened by the bubble-menu pencil button; the modal
  // itself lives inside SlotGroupEditor (embedded mode).
  const [editOpen, setEditOpen] = useState(false);

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        select(node.id);
      }}
      className={`relative rounded border px-px pb-0.5 pt-2 transition ${
        isSelected
          ? 'border-amber-400 bg-amber-50/40 dark:border-amber-500 dark:bg-amber-950/20'
          : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900'
      }`}
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400">
        <FileText className="h-3 w-3" />
        Slot · {selectedGroup?.name ?? '(ยังไม่ได้เลือก group)'}
        <span className="rounded bg-slate-100 px-1.5 text-[9px] text-slate-500 dark:bg-slate-800 dark:text-slate-400">
          #{effectiveIndex} · {effectivePreset}
        </span>
      </div>

      <NodeToolbar
        visible={isSelected}
        extra={
          <div className="mr-1 flex items-center gap-1 border-r border-slate-200 pr-2 dark:border-slate-700">
            <select
              value={node.groupSlug}
              onChange={(e) => updateSlot(node.id, { groupSlug: e.target.value })}
              onClick={(e) => e.stopPropagation()}
              title="เลือก Group Content"
              className="border-input bg-background h-7 max-w-[220px] rounded border px-1.5 text-xs"
            >
              <option value="">— เลือก group —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.slug}>
                  {g.name}
                </option>
              ))}
            </select>
            <select
              value={effectiveIndex}
              onChange={(e) => updateSlot(node.id, { itemIndex: Number(e.target.value) })}
              onClick={(e) => e.stopPropagation()}
              title="เลือก item ตัวที่แสดง"
              className="border-input bg-background h-7 w-[120px] rounded border px-1.5 text-xs"
              disabled={!selectedGroup}
            >
              {itemOptions.map((i) => (
                <option key={i} value={i}>
                  #{i} - ข่าวที่ {i + 1}
                </option>
              ))}
            </select>
            <select
              value={effectivePreset}
              onChange={(e) => updateSlot(node.id, { preset: e.target.value as SlotPreset })}
              onClick={(e) => e.stopPropagation()}
              title="Preset (รูปแบบการแสดง item)"
              className="border-input bg-background h-7 w-[100px] rounded border px-1.5 text-xs"
            >
              {SLOT_PRESETS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            {selectedGroup && (
              <Link
                to="/group-contents/$id/edit"
                params={{ id: selectedGroup.id }}
                title="แก้ Group (tab ใหม่)"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="border-input rounded border p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>
        }
        buttons={[
          {
            key: 'edit',
            label: 'แก้ไข',
            icon: <Pencil className="h-3.5 w-3.5" />,
            onClick: () => setEditOpen(true),
            disabled: !selectedGroup,
          },
          {
            key: 'delete',
            label: 'ลบ',
            icon: <Trash2 className="h-3.5 w-3.5" />,
            onClick: () => remove(node.id),
            variant: 'destructive',
          },
        ]}
      />

      <div className="space-y-2">
        {!node.groupSlug && (
          <div className="rounded border border-dashed border-slate-300 px-2 py-3 text-center text-xs text-slate-400 dark:border-slate-700">
            ยังไม่ได้เลือก Group Content — คลิกที่ slot นี้แล้วเลือกจาก toolbar ด้านบน
          </div>
        )}

        {!selectedGroup && node.groupSlug && (
          <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
            ⚠ group "<code>{node.groupSlug}</code>" ไม่พบ
          </div>
        )}

        {selectedGroup && (
          <div onClick={(e) => e.stopPropagation()}>
            {/* Single-item preview rendered per slot.preset — mirrors how the
                public site will render this slot. */}
            {previewQuery.isLoading ? (
              <div className="rounded border border-dashed border-slate-200 px-2 py-1 text-center text-[10px] text-slate-400 dark:border-slate-700">
                กำลังโหลด preview…
              </div>
            ) : previewItems.length === 0 ? (
              <div className="rounded border border-dashed border-amber-300 bg-amber-50 px-2 py-1 text-center text-[10px] text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
                Group ยังไม่มี item — คลิก ✏️ แก้ไข เพื่อเพิ่ม
              </div>
            ) : effectiveIndex >= previewItems.length ? (
              <div className="rounded border border-dashed border-amber-300 bg-amber-50 px-2 py-1 text-center text-[10px] text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
                Group มี {previewItems.length} items — ตำแหน่ง #{effectiveIndex} ไม่มีข้อมูล
              </div>
            ) : (
              <ItemSlot item={selectedItem} variant={effectivePreset} />
            )}
            {/* Edit modal (embedded — controlled by the bubble-menu pencil) */}
            <SlotGroupEditor
              groupId={selectedGroup.id}
              layoutTemplateId={null}
              embedded={{ open: editOpen, onOpenChange: setEditOpen }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
